import { useState } from 'react';
import { RoomApiError, roomFetch } from './roomFetch';
import { OtpForm } from './OtpForm';

/**
 * The email gate — the whole of the Room's authentication.
 *
 * Two steps: confirm the address the professional recorded (→ a code is emailed), then enter the
 * code (→ a Room session). The link itself is not a secret, so this is what actually protects the
 * client's answers.
 *
 * THE NEUTRALITY THE SERVER GUARANTEES, HONOURED IN THE UI: a wrong address returns a `challengeId`
 * exactly like a right one, and the wording here never says whether the address matched. It says "if
 * this is the right address, a code is on its way". Promising a code unconditionally is the only way
 * the page cannot become an address-confirmation oracle for whoever holds the link — the same reason
 * the backend mints a decoy id on a mismatch.
 */

/** The active-link half of POST /room/resolve. */
interface ActiveLink {
  clientName: string;
  emailHint: string;
}

export function EmailGate({
  instanceId,
  link,
  onVerified,
}: {
  instanceId: string;
  link: ActiveLink;
  /** Called once the OTP is accepted and the room token is stored. RoomApp swaps in the wizard. */
  onVerified: (token: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (challengeId) {
    return (
      <OtpForm
        challengeId={challengeId}
        emailHint={link.emailHint}
        onVerified={onVerified}
        onBack={() => {
          // "Wrong address / didn't arrive" — drop the challenge and let them re-enter the email.
          // Their previous code silently expires server-side; there is nothing to clean up here.
          setChallengeId(null);
          setError(null);
        }}
      />
    );
  }

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (!email.trim()) {
      setError('הזינו את כתובת המייל שאליה נשלח הקישור.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const { challengeId: id } = await roomFetch<{ challengeId: string }>('room/otp/request', {
        body: { instanceId, email: email.trim() },
      });
      // Reached for a matching AND a non-matching address alike (the server is neutral). We advance
      // to the code step either way — if the address was wrong no mail arrives, and the visitor
      // works that out for themselves rather than being told.
      setChallengeId(id);
    } catch (err) {
      // A real failure worth surfacing: the link died between resolve and now, or the per-instance
      // request cap tripped. Neither reveals anything about the address.
      if (err instanceof RoomApiError && err.status === 429) {
        setError('נשלחו כבר מספר קודים. המתינו כמה דקות ונסו שוב.');
      } else if (err instanceof RoomApiError && (err.code === 'NOT_FOUND' || err.status === 404)) {
        setError('הקישור אינו פעיל עוד. פנו לאיש הקשר שלכם.');
      } else {
        setError(err instanceof RoomApiError ? err.message : 'משהו השתבש. נסו שוב.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="room-card">
      <h1 className="room-card-title">שלום {link.clientName}</h1>
      <p className="room-card-body">
        כדי להיכנס, הזינו את כתובת המייל שאליה נשלח הקישור. נשלח אליכם קוד אימות חד-פעמי.
      </p>

      <form className="room-form" onSubmit={requestCode} noValidate>
        <label className="room-field">
          <span className="room-field-label">כתובת מייל</span>
          <input
            className="room-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            // The address is Latin script inside a Hebrew page; keep it LTR on the field only.
            dir="ltr"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={error !== null}
            autoFocus
          />
        </label>

        {error && (
          <p className="room-error" role="alert">
            {error}
          </p>
        )}

        <div className="room-actions">
          <button type="submit" className="btn-primary" disabled={busy}>
            {busy ? 'שולח…' : 'שליחת קוד'}
          </button>
        </div>
      </form>
    </section>
  );
}
